import React from 'react';
import { observer } from 'mobx-react-lite';
import { Link as RouterLink, LinkProps, NavLink as RouterNavLink, NavLinkProps, To } from 'react-router-dom';
import { Maybe } from '../../core';
import { LocationDescriptor } from '../../routes';

/** Extract state from LocationDescriptor (React Router v7 requires state as separate prop) */
function extractLocationState<S>(to: To | LocationDescriptor<S>): { routerTo: To; routerState?: S } {
  if (to && typeof to === 'object' && 'state' in to) {
    const { state, ...pathProps } = to as LocationDescriptor<S>;
    return { routerTo: pathProps as To, routerState: state };
  }
  return { routerTo: to as To };
}

type Props = Omit<LinkProps, 'to'> & {
  enabled?: boolean;
  to?: Maybe<To | LocationDescriptor>;
}

/** Wrapper around react router Link which makes it usable in real life scenarios. */
export const Link = observer(({
  enabled = true,
  children,
  to,
  replace,
  state: stateProp,
  ...props
}: Props) => {

  if (!to)
    enabled = false;

  // Extract state from LocationDescriptor if present
  const { routerTo, routerState } = to ? extractLocationState(to) : { routerTo: undefined, routerState: undefined };
  const finalState = stateProp ?? routerState;

  if (enabled && routerTo) {
    return (
      <RouterLink
        to={routerTo}
        replace={replace}
        state={finalState}
        {...props}>

        {children}
      </RouterLink>
    )
  }

  return (
    <div {...props as React.HTMLAttributes<HTMLDivElement>}>
      {children}
    </div>
  );
});

type NavLinkWrapperProps = Omit<NavLinkProps, 'to'> & {
  to: To | LocationDescriptor;
};

/** Wrapper around react router NavLink that handles LocationDescriptor state extraction. */
export function NavLink({ to, state: stateProp, ...props }: NavLinkWrapperProps) {
  const { routerTo, routerState } = extractLocationState(to);
  const finalState = stateProp ?? routerState;

  return <RouterNavLink to={routerTo} state={finalState} {...props} />;
}